import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const TeacherGrievance = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('submit');
  const [grievances, setGrievances] = useState([]);
  const [filteredGrievances, setFilteredGrievances] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Teacher profile data
  const [teacherData, setTeacherData] = useState({
    name: "Loading...",
    teacherId: "...",
    department: "Computer Science",
    designation: "Lecturer",
    profileImage: null
  });

  // Form states
  const [grievanceForm, setGrievanceForm] = useState({
    title: '',
    category: '',
    priority: 'medium',
    description: '',
    attachments: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  // Categories mapped to database enum values - Updated to include Leave Application
  const categories = [
    { value: 'academic', label: '📚 Academic Issues' },
    { value: 'administrative', label: '📋 Administrative Matters' },
    { value: 'technical', label: '💻 Technical Support' },
    { value: 'infrastructure', label: '🏢 Infrastructure Problems' },
    { value: 'salary', label: '💰 Salary & Benefits' },
    { value: 'workload', label: '⏰ Workload & Schedule' },
    { value: 'leave', label: '🏖️ Leave Application' },
    { value: 'harassment', label: '🚨 Harassment/Discrimination' },
    { value: 'other', label: '📝 Other Issues' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: '#28a745' },
    { value: 'medium', label: 'Medium', color: '#ffc107' },
    { value: 'high', label: 'High', color: '#fd7e14' },
    { value: 'urgent', label: 'Urgent', color: '#dc3545' }
  ];

  const statuses = [
    { value: 'pending', label: 'Pending', color: '#6c757d' },
    { value: 'in-progress', label: 'In Progress', color: '#007bff' },
    { value: 'resolved', label: 'Resolved', color: '#28a745' }
  ];

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get authentication token
  const getAuthToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  };

  // Fetch teacher profile data
  useEffect(() => {
    const fetchTeacherProfile = async () => {
      try {
        console.log('🔍 Fetching teacher profile data...');

        const token = getAuthToken();
        if (!token) {
          console.log('❌ No token found for profile fetch');
          return;
        }

        const response = await fetch(`${API_LINK}/api/teacher/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            console.log('✅ Teacher profile data loaded:', result.data);
            setTeacherData({
              name: result.data.name,
              teacherId: result.data.teacherId,
              department: result.data.department,
              designation: result.data.designation,
              profileImage: result.data.profileImage
            });
          }
        } else {
          console.log('❌ Failed to fetch profile:', response.status);
        }
      } catch (error) {
        console.error('💥 Error fetching teacher profile:', error);
      }
    };

    // Fetch profile data immediately
    fetchTeacherProfile();
  }, []);

  // Load grievances data from API
  useEffect(() => {
    const loadGrievances = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting to fetch teacher grievances from API...');

        // Get token from storage
        const token = getAuthToken();

        if (!token) {
          throw new Error('Authentication token not found. Please login again.');
        }

        console.log('🔑 Token found, making API request...');

        const response = await fetch(`${API_LINK}/api/teacher/grievances`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 API Response status:', response.status);

        if (!response.ok) {
          if (response.status === 401) {
            console.log('🔐 Authentication failed');
            throw new Error('Session expired. Please login again.');
          }
          if (response.status === 404) {
            console.log('🔍 API endpoint not found');
            throw new Error('API endpoint not found. Please check server configuration.');
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('📦 API Response:', result);

        if (result.success && result.data) {
          console.log('✅ Grievances loaded successfully:', result.data.length, 'items');
          setGrievances(result.data);
        } else {
          console.log('❌ API returned error:', result.message);
          throw new Error(result.message || 'Failed to fetch grievances');
        }

      } catch (error) {
        console.error('💥 Error fetching grievances:', error);

        if (error.message.includes('fetch') || error.message.includes('NetworkError')) {
          setError('Cannot connect to server. Please ensure the server is running ');
        } else if (error.message.includes('token') || error.message.includes('login')) {
          setError('Session expired. Please login again.');
          // Auto-logout after 3 seconds
          setTimeout(() => {
            handleLogout();
          }, 3000);
        } else {
          setError(error.message || 'Failed to load grievances. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    // Add delay to ensure component is mounted
    const timer = setTimeout(() => {
      loadGrievances();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Filter grievances based on search and filters
  useEffect(() => {
    let filtered = [...grievances];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(grievance =>
        grievance.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grievance.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grievance.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(grievance => grievance.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(grievance => grievance.category === categoryFilter);
    }

    setFilteredGrievances(filtered);
  }, [grievances, searchTerm, statusFilter, categoryFilter]);

  // Handle logout
  const handleLogout = () => {
    console.log('🚪 Logging out...');
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };



  // Handle form input changes
  const handleInputChange = (field, value) => {
    setGrievanceForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear any error messages when user starts typing
    if (submitError) {
      setSubmitError('');
    }
    if (submitMessage) {
      setSubmitMessage('');
    }
  };

  // Handle file upload - Updated to only accept PDF files within 3MB
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);

    // Clear previous errors
    setSubmitError('');

    // Validate each file individually
    for (let file of files) {
      // Check file size (3MB limit per file)
      if (file.size > 3 * 1024 * 1024) {
        setSubmitError(`File "${file.name}" is larger than 3MB. Please choose a smaller PDF file.`);
        event.target.value = ''; // Clear the input
        return;
      }

      // Check file extension
      const fileExtension = file.name.toLowerCase().split('.').pop();
      if (fileExtension !== 'pdf') {
        setSubmitError(`File "${file.name}" is not a PDF file. Only PDF documents are allowed.`);
        event.target.value = ''; // Clear the input
        return;
      }

      // Check MIME type
      if (file.type !== 'application/pdf' && file.type !== '') {
        setSubmitError(`File "${file.name}" has an invalid format. Only PDF documents are allowed.`);
        event.target.value = ''; // Clear the input
        return;
      }

      // Additional check for file signature (magic numbers) for PDF
      const reader = new FileReader();
      reader.onload = function (e) {
        const arr = new Uint8Array(e.target.result);
        // Check for PDF signature: %PDF
        if (arr.length >= 4) {
          const signature = String.fromCharCode(arr[0], arr[1], arr[2], arr[3]);
          if (signature !== '%PDF') {
            setSubmitError(`File "${file.name}" is not a valid PDF document.`);
            event.target.value = ''; // Clear the input
            return;
          }
        }
      };
      reader.readAsArrayBuffer(file.slice(0, 4));
    }

    // If all validations pass, add files to form
    setGrievanceForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));

    console.log('📁 PDF files will be stored in: /uploads/grievances/pdfs/');
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setGrievanceForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  // Submit grievance to API
  const handleSubmitGrievance = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError('');
      setSubmitMessage('');

      // Validate form
      if (!grievanceForm.title.trim()) {
        setSubmitError('Please enter a title for your grievance');
        return;
      }

      if (!grievanceForm.category) {
        setSubmitError('Please select a category');
        return;
      }

      if (!grievanceForm.description.trim()) {
        setSubmitError('Please enter a description');
        return;
      }

      if (grievanceForm.description.trim().length < 20) {
        setSubmitError('Description must be at least 20 characters long');
        return;
      }

      console.log('🔄 Submitting grievance to API...');

      // Get token
      const token = getAuthToken();

      if (!token) {
        setSubmitError('Authentication token not found. Please login again.');
        setTimeout(() => handleLogout(), 2000);
        return;
      }

      // Prepare form data for multipart upload
      const formData = new FormData();

      // Add grievance data as JSON string
      const grievanceData = {
        title: grievanceForm.title.trim(),
        category: grievanceForm.category,
        priority: grievanceForm.priority,
        description: grievanceForm.description.trim()
      };

      formData.append('grievanceData', JSON.stringify(grievanceData));

      // Add attachment files
      if (grievanceForm.attachments.length > 0) {
        grievanceForm.attachments.forEach((file, index) => {
          formData.append('attachments', file);
          console.log(`📎 Adding attachment ${index + 1}:`, file.name);
        });
      }

      console.log('📤 Sending request to server...');

      const response = await fetch(`${API_LINK}/api/teacher/grievances`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData
      });

      console.log('📡 Submit response status:', response.status);

      if (!response.ok) {
        if (response.status === 401) {
          setSubmitError('Session expired. Please login again.');
          setTimeout(() => handleLogout(), 2000);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('📦 Submit response:', result);

      if (result.success && result.data) {
        console.log('✅ Grievance submitted successfully:', result.data.id);

        // Add new grievance to the list
        setGrievances(prev => [result.data, ...prev]);

        // Reset form
        setGrievanceForm({
          title: '',
          category: '',
          priority: 'medium',
          description: '',
          attachments: []
        });

        // Clear file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
        }

        setSubmitMessage(result.message || 'Grievance submitted successfully! You will receive updates on its progress.');

        // Switch to track tab to show the new grievance
        setTimeout(() => {
          setActiveTab('track');
          setSubmitMessage('');
        }, 3000);

      } else {
        throw new Error(result.message || 'Failed to submit grievance');
      }

    } catch (error) {
      console.error('💥 Error submitting grievance:', error);

      if (error.message.includes('fetch') || error.message.includes('NetworkError')) {
        setSubmitError('Cannot connect to server. Please check your connection and try again.');
      } else if (error.message.includes('401') || error.message.includes('token')) {
        setSubmitError('Session expired. Please login again.');
        setTimeout(() => handleLogout(), 2000);
      } else {
        setSubmitError(error.message || 'Failed to submit grievance. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };



  // Get category label
  const getCategoryLabel = (category) => {
    const categoryObj = categories.find(cat => cat.value === category);
    return categoryObj ? categoryObj.label : category;
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const priorityObj = priorities.find(p => p.value === priority);
    return priorityObj ? priorityObj.color : '#6c757d';
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusObj = statuses.find(s => s.value === status);
    return statusObj ? statusObj.color : '#6c757d';
  };

  // Get status label
  const getStatusLabel = (status) => {
    const statusObj = statuses.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Grievance System...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '500px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Grievance System
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e7139';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#238548';
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#666';
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-grievance" userInfo={userInfo}>
      {/* Header Card */}
      <div style={{
        background: 'linear-gradient(135deg, #238548, #1a6b38)',
        borderRadius: '10px',
        padding: isMobile ? '1.2rem' : '1.5rem 2rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
      }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? '1.3rem' : '1.6rem', fontWeight: '700', color: '#fff' }}>📢 Grievance Portal</h1>
        <p style={{ margin: '0.3rem 0 0', color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? '0.82rem' : '0.9rem' }}>
          Submit and track your grievances
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        background: '#ffffff',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
        border: '1px solid #eef2f6',
        marginBottom: '1.5rem',
        overflow: 'hidden'
      }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #e9ecef' }}>
          <button
            onClick={() => setActiveTab('submit')}
            style={{
              flex: 1,
              padding: isMobile ? '0.8rem 1rem' : '1rem 2rem',
              border: 'none',
              background: activeTab === 'submit' ? '#238548' : 'transparent',
              color: activeTab === 'submit' ? '#ffffff' : '#666',
              fontSize: isMobile ? '0.88rem' : '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <span>📝</span>
            {isMobile ? 'Submit' : 'Submit Grievance'}
          </button>
          <button
            onClick={() => setActiveTab('track')}
            style={{
              flex: 1,
              padding: isMobile ? '0.8rem 1rem' : '1rem 2rem',
              border: 'none',
              background: activeTab === 'track' ? '#238548' : 'transparent',
              color: activeTab === 'track' ? '#ffffff' : '#666',
              fontSize: isMobile ? '0.88rem' : '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <span>📋</span>
            {isMobile ? `Track (${grievances.length})` : `Track Grievances (${grievances.length})`}
          </button>
        </div>
      </div>

      {/* Submit Grievance Tab */}
      {activeTab === 'submit' && (
        <div style={{
          background: '#ffffff',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
          border: '1px solid #eef2f6',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #238548, #1a6b38)',
            color: '#ffffff',
            padding: isMobile ? '1rem 1.2rem' : '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.4rem' : '1.8rem' }}>📝</span>
            <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.2rem' }}>Submit New Grievance</h3>
          </div>

          <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
            {/* Success/Error Messages */}
            {(submitMessage || submitError) && (
              <div style={{
                background: submitMessage ? '#d4edda' : '#f8d7da',
                border: `1px solid ${submitMessage ? '#c3e6cb' : '#f5c6cb'}`,
                color: submitMessage ? '#155724' : '#721c24',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1.2rem' }}>
                  {submitMessage ? '✅' : '❌'}
                </span>
                {submitMessage || submitError}
              </div>
            )}

            {/* Form */}
            <div style={{
              display: 'grid',
              gap: '1.5rem'
            }}>
              {/* Title */}
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '0.5rem'
                }}>
                  Grievance Title *
                </label>
                <input
                  type="text"
                  value={grievanceForm.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter a brief title for your grievance"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#238548';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
              </div>

              {/* Category and Priority */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '1rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '0.5rem'
                  }}>
                    Category *
                  </label>
                  <select
                    value={grievanceForm.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: '#fff'
                    }}
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '0.5rem'
                  }}>
                    Priority
                  </label>
                  <select
                    value={grievanceForm.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: '#fff'
                    }}
                  >
                    {priorities.map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '0.5rem'
                }}>
                  Description *
                </label>
                <textarea
                  value={grievanceForm.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Please provide detailed information about your grievance..."
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'border-color 0.3s ease',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#238548';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
                <div style={{
                  fontSize: '0.8rem',
                  color: '#666',
                  marginTop: '0.25rem'
                }}>
                  {grievanceForm.description.length}/500 characters (minimum 20 required)
                </div>
              </div>

              {/* File Attachments - Updated for PDF only */}
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '0.5rem'
                }}>
                  Attachments (Optional)
                </label>
                <input
                  type="file"
                  multiple
                  accept="application/pdf,.pdf"
                  onChange={handleFileUpload}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px dashed #dc3545',
                    borderRadius: '8px',
                    backgroundColor: '#fff5f5',
                    cursor: 'pointer'
                  }}
                />
                <div style={{
                  fontSize: '0.8rem',
                  color: '#dc3545',
                  marginTop: '0.25rem',
                  fontWeight: '600'
                }}>
                  ⚠️ ONLY PDF files allowed (Max 3MB per file) - Files will be stored in /uploads/grievances/pdfs/
                </div>

                {/* Attachment List */}
                {grievanceForm.attachments.length > 0 && (
                  <div style={{
                    marginTop: '1rem',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '1rem'
                  }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', color: '#333' }}>
                      Attached Files ({grievanceForm.attachments.length})
                    </h4>
                    {grievanceForm.attachments.map((file, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        marginBottom: '0.5rem'
                      }}>
                        <div>
                          <span style={{ fontWeight: '500' }}>📄 {file.name}</span>
                          <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          onClick={() => removeAttachment(index)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.25rem 0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button
                  onClick={handleSubmitGrievance}
                  disabled={isSubmitting}
                  style={{
                    background: isSubmitting ? '#6c757d' : 'linear-gradient(135deg, #238548, #1a6b38)',
                    color: '#ffffff',
                    border: 'none',
                    padding: '1rem 2rem',
                    borderRadius: '10px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    margin: '0 auto',
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: 'center',
                    boxShadow: '0 4px 15px rgba(35,133,72,0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.background = '#1e7139';
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.target.style.background = '#238548';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <span>📤</span>
                      Submit Grievance
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Track Grievances Tab */}
      {activeTab === 'track' && (
        <div style={{
          background: '#ffffff',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
          border: '1px solid #eef2f6',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #238548, #1a6b38)',
            color: '#ffffff',
            padding: isMobile ? '1rem 1.2rem' : '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.4rem' : '1.8rem' }}>📋</span>
            <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.2rem' }}>Track Your Grievances</h3>
          </div>

          {/* Filters */}
          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid #e9ecef',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <input
              type="text"
              placeholder="Search grievances..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '1rem',
                backgroundColor: '#fff'
              }}
            >
              <option value="all">All Statuses</option>
              {statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '1rem',
                backgroundColor: '#fff'
              }}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* Grievances List */}
          <div style={{ padding: isMobile ? '1rem' : '1.5rem' }}>
            {filteredGrievances.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#666'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                <h3 style={{ margin: '0 0 0.5rem 0' }}>No Grievances Found</h3>
                <p style={{ margin: 0 }}>
                  {grievances.length === 0
                    ? "You haven't submitted any grievances yet."
                    : "No grievances match your current filters."
                  }
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '1.5rem'
              }}>
                {filteredGrievances.map((grievance) => (
                  <div key={grievance.id} style={{
                    border: '1px solid #e9ecef',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                    {/* Grievance Header */}
                    <div style={{
                      background: '#f8f9fa',
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid #e9ecef',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '1rem'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          margin: '0 0 0.5rem 0',
                          color: '#333',
                          fontSize: '1.1rem'
                        }}>
                          {grievance.title}
                        </h4>
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          flexWrap: 'wrap',
                          fontSize: '0.9rem',
                          color: '#666'
                        }}>
                          <span>📅 {formatDate(grievance.submittedDate)}</span>
                          <span>🆔 {grievance.id}</span>
                          <span>{getCategoryLabel(grievance.category)}</span>
                        </div>
                      </div>

                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{
                          background: getPriorityColor(grievance.priority),
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '15px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {grievance.priority.toUpperCase()}
                        </span>
                        <span style={{
                          background: getStatusColor(grievance.status),
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '15px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {getStatusLabel(grievance.status)}
                        </span>
                      </div>
                    </div>

                    {/* Grievance Body */}
                    <div style={{ padding: '1.5rem' }}>
                      <p style={{
                        margin: '0 0 1rem 0',
                        color: '#666',
                        lineHeight: 1.6
                      }}>
                        {grievance.description}
                      </p>

                      {/* Responses */}
                      {grievance.responses && grievance.responses.length > 0 && (
                        <div style={{
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          padding: '1rem',
                          marginTop: '1rem'
                        }}>
                          <h5 style={{
                            margin: '0 0 1rem 0',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <span>💬</span>
                            Updates & Responses
                          </h5>
                          {grievance.responses.map((response, index) => (
                            <div key={index} style={{
                              background: 'white',
                              padding: '0.75rem',
                              borderRadius: '6px',
                              marginBottom: index < grievance.responses.length - 1 ? '0.75rem' : 0,
                              borderLeft: `4px solid ${response.type === 'resolution' ? '#28a745' : '#007bff'}`
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '0.5rem',
                                flexWrap: 'wrap',
                                gap: '0.5rem'
                              }}>
                                <span style={{
                                  fontWeight: '600',
                                  color: '#333',
                                  fontSize: '0.9rem'
                                }}>
                                  {response.responder}
                                </span>
                                <span style={{
                                  fontSize: '0.8rem',
                                  color: '#666'
                                }}>
                                  {formatDate(response.date)}
                                </span>
                              </div>
                              <p style={{
                                margin: 0,
                                color: '#666',
                                fontSize: '0.9rem',
                                lineHeight: 1.5
                              }}>
                                {response.message}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default TeacherGrievance;